// src/ClaimRewards.js
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { formatEther } from "ethers";

function ClaimRewards({ contract, signerContract }) {
    const [topics, setTopics] = useState([]);
    const [totalClaimable, setTotalClaimable] = useState(0n); // To store the total claimable amount
    const [message, setMessage] = useState("");
    const [log, setLog] = useState([]);


    useEffect(() => {
        const init = async () => {
            if (contract) {
                loadTopics(signerContract);

                contract.on("TopicCreated", (topicId, creator) => {
                    console.log("TopicCreated event received:", { topicId, creator });
                    handleNewTopic(contract, topicId.toString(), creator);
                });

                contract.on("TopicExecuted", (topicId) => {
                    console.log("TopicExecuted event received:", { topicId });
                    handleTopicExecuted(topicId);
                });
            }
        };

        init();
        // Cleanup listener on component unmount
        return () => {
            // Clean up any contract listeners if necessary
        };
    }, [signerContract]);


    const handleTopicExecuted = async (topicId) => {
        setTopics((prevTopics) => prevTopics.map((topic) => {
            if (topic.id === topicId.toString()) {
                return {
                    ...topic,
                    isExecuted: true,
                };
            }
            return topic;
        }));
    };

    const handleNewTopic = async (contractInstance, topicId, creator) => {
        const topic = await contractInstance.getTopic(topicId);
        const currentTime = Math.floor(Date.now() / 1000);
        const formattedTopic = await formatTopic(contractInstance, topic);

        setTopics((prevTopics) => {
            if (!prevTopics.some(t => t.id === topicId.toString())) {
                return [formattedTopic, ...prevTopics];
            }
            return prevTopics;
        });
    };
    const formatTopic = async (contractInstance, topic) => {
        if (!contractInstance) {
            console.error("Contract is not initialized.");
            return; // Early return to prevent further errors
        }
        if (!topic) {
            console.error("Received null topic");
            return;
        }
        // Fetch the potential claim amount using the new calculateClaim function
        let rewardAmount = 0n;
        let refundAmount = 0n;
        if (topic.isExecuted) [rewardAmount, refundAmount] = await contractInstance.calculateClaim(topic.id);
        const hasClaim = rewardAmount > 0n || refundAmount > 0n; // Use BigInt comparison

        // Convert all BigInt values to strings for easier manipulation
        return {
            id: topic.id.toString(),
            question: topic.question,
            rewardAmount: rewardAmount.toString(),
            refundAmount: refundAmount.toString(),
            hasClaim: hasClaim,
            isExecuted: topic.isExecuted,
        };
    };

    const loadTopics = async (contractInstance) => {
        const topicsCount = await contractInstance.getTopicCounter();
        let topics = [];
        let newTotalClaimable = 0n; // Start with 0

        for (let i = 0; i < topicsCount; i++) {
            const topic = await contractInstance.getTopic(i);

            // Skip topics that have not been executed
            // if (!topic.isExecuted) continue;
            const formattedTopic = await formatTopic(contractInstance, topic);
            if (formattedTopic.hasClaim) {
                // Accumulate the claimable amount
                newTotalClaimable += BigInt(formattedTopic.rewardAmount) + BigInt(formattedTopic.refundAmount);
            }
            // console.log(` Topic #${i}:`, formattedTopic);
            topics.push(formattedTopic);
        }
        setTotalClaimable(newTotalClaimable); // Update total claimable
        setTopics(topics.reverse());
    };
    const handleClaimRewardOrRefund = async (topicId) => {
        try {
            const tx = await signerContract.claim(topicId);
            await tx.wait();
            const newLog = {
                action: `Claim Reward or Refund for Topic ${topicId}`,
                timestamp: new Date().toLocaleString(),
                status: "success",
            };
            setLog([newLog, ...log.slice(0, 4)]);
            setMessage("Reward or refund claimed successfully!");
            loadTopics(contract);
        } catch (error) {
            console.error("Error claiming reward or refund:", error);
            const newLog = {
                action: `Claim Reward or Refund for Topic ${topicId}`,
                timestamp: new Date().toLocaleString(),
                status: "Failed with error: " + (error.message || "Unknown error"),
            };
            setLog([newLog, ...log.slice(0, 4)]);
            setMessage("Failed to claim reward or refund.");
        }
    };
    const handleClaimAllRewards = async () => {
        try {
            const tx = await signerContract.claimAll();
            await tx.wait();

            const newLog = {
                action: `Claim All Rewards`,
                timestamp: new Date().toLocaleString(),
                status: "success",
            };

            setLog([newLog, ...log.slice(0, 4)]);
            setMessage("All rewards and refunds claimed successfully!");

            // Reload topics to reflect the new state
            loadTopics(contract);
        } catch (error) {
            console.error("Error claiming all rewards:", error);
            setMessage("Failed to claim all rewards.");
            const newLog = {
                action: `Claim All Rewards`,
                timestamp: new Date().toLocaleString(),
                status: "Failed with error: " + (error.message || "Unknown error"),
            };
            setLog([newLog, ...log.slice(0, 4)]);
        }
    };
    return (
        <div className="container mt-5">
            <Helmet>
                <title>6Degree - Claim Your Blockchain-based Rewards</title>
                <meta name="description" content="Claim your earned rewards securely on the 6Degree platform. Join the decentralized revolution and enjoy the benefits." />
            </Helmet>
            <h1>Claim Your Rewards or Refunds</h1>
            {totalClaimable > 0n && (
                <button
                    onClick={handleClaimAllRewards}
                    className="btn btn-primary mb-4"
                >
                    Claim All Wins ({formatEther(totalClaimable.toString())} 6DO)
                </button>
            )}
            {topics.map((topic) => (
                <div key={topic.id} className="mb-3">
                    <h5>
                        Topic #{topic.id}: {topic.question}
                    </h5>
                    {!topic.isExecuted ? (
                        <p>Not yet executed</p>
                    ) : topic.hasClaim ? (
                        <div>
                            <p>
                                Potential Reward: {formatEther(topic.rewardAmount)} ETH
                            </p>
                            <p>
                                Potential Refund: {formatEther(topic.refundAmount)} ETH
                            </p>
                            <button
                                onClick={() => handleClaimRewardOrRefund(topic.id)}
                                className="btn btn-success"
                            >
                                Claim Reward/Refund
                            </button>
                        </div>
                    ) : (
                        <p>No claimable rewards or refunds.</p>
                    )}
                </div>
            ))}
            {message && <div className="alert alert-info mt-4">{message}</div>}
            <div className="mt-5">
                <h4>Log</h4>
                <ul>
                    {log.map((entry, index) => (
                        <li key={index} style={{ color: entry.status === "fail" ? "red" : "black" }}>
                            [{entry.timestamp}] {entry.action} - {entry.status}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}


export default ClaimRewards;